// Additional services + blog posts (added Dec 2025)
// Each service follows the same shape as `serviceDetails` in blogData.js
// Each blog post follows the same shape as `blogPosts` in blogData.js
// and is written to Google's helpful-content + E-E-A-T guidelines.

const CITY = "Brooklyn, NY";

export const additionalServiceDetails = {
  "circuit-box-repairs": {
    title: "Circuit Box Repairs",
    subtitle: `Circuit Breaker & Fuse Box Repair Service in ${CITY}`,
    image: "https://images.pexels.com/photos/32497160/pexels-photo-32497160.jpeg",
    overview: "A faulty circuit box puts your home at risk of fire, shock, and power loss. Jetco Electrical Contractors diagnoses and repairs tripping breakers, hot panels, burnt bus bars, double-tapped breakers, and aging fuse boxes across Brooklyn, Queens, and Manhattan. Same-day visits, code-compliant repairs, and transparent flat-rate pricing.",
    benefits: [
      "Same-day diagnosis for tripping or buzzing breakers",
      "Replacement of failed breakers, lugs, and bus bars",
      "Removal of unsafe double-tapped or recalled breakers (FPE, Zinsco)",
      "Insured, experienced NYC electricians — every repair NEC code-compliant",
      "Free written estimate before any work begins",
      "1-year workmanship warranty on all panel repairs",
    ],
    process: [
      { step: 1, title: "On-Site Diagnosis", description: "We thermal-scan the panel, test breakers under load, and identify the root cause — not just the symptom." },
      { step: 2, title: "Written Estimate", description: "You receive a flat-rate quote for the repair plus any code corrections required by NYC DOB." },
      { step: 3, title: "Repair & Replacement", description: "We replace failed components, retorque lugs to manufacturer spec, and clean any oxidation." },
      { step: 4, title: "Load Test & Sign-Off", description: "We re-energize the panel, verify amperage across phases, and walk you through the work." },
    ],
    faqs: [
      { question: "Is a tripping breaker dangerous?", answer: "A breaker that trips once in a while is doing its job. A breaker that trips repeatedly, feels hot, or smells like burnt plastic indicates a real fault and needs immediate professional inspection." },
      { question: "How much does a circuit box repair cost in Brooklyn?", answer: "Most single-breaker replacements run $180–$350. Bus-bar repairs, retorquing, or removing recalled FPE/Zinsco panels range from $400–$1,400. We always provide a written estimate before starting." },
      { question: "Do I need a permit for panel repairs?", answer: "Like-for-like breaker replacements typically do not require a permit. Bus-bar, lug, or main-disconnect work usually does — we handle the NYC DOB filing on your behalf." },
      { question: "Can you repair an old Federal Pacific or Zinsco panel?", answer: "We strongly recommend replacing FPE Stab-Lok and Zinsco panels — both have documented fire histories. We can quote a panel swap the same day." },
    ],
    relatedServices: ["electrical-contracting", "generator-installation", "residential-and-commercial-electrical-service"],
  },

  "electrical-contracting": {
    title: "Electrical Contracting",
    subtitle: `Full-Service Electrical Contractor in ${CITY}`,
    image: "https://images.pexels.com/photos/5667685/pexels-photo-5667685.jpeg",
    overview: "From a single outlet to a ground-up commercial build, Jetco Electrical Contractors delivers turnkey electrical contracting across NYC. We handle load calculations, plan filing, permits, rough-in, finish work, and DOB inspections so general contractors and property owners can focus on the build, not the wiring.",
    benefits: [
      "Single point of contact from plan filing through final sign-off",
      "Experienced electricians on every project",
      "Plan & permit filing with NYC DOB and ConEdison",
      "Coordination with GCs, architects, and inspectors",
      "Competitive lump-sum and time-and-materials pricing",
      "Fully bonded and insured",
    ],
    process: [
      { step: 1, title: "Pre-Construction Walk-Through", description: "We review architectural plans, perform load calculations, and identify any service upgrades required." },
      { step: 2, title: "Plans, Permits & Filing", description: "Our office files with the NYC DOB and coordinates ConEd service requests." },
      { step: 3, title: "Rough-In & Inspection", description: "We pull conduit, run circuits, set boxes, and stage for closed-wall and rough inspection." },
      { step: 4, title: "Trim Out & Final Sign-Off", description: "Devices are installed, panels are labeled, and we walk the final inspection with the DOB inspector." },
    ],
    faqs: [
      { question: "Do you handle electrical plan filing in NYC?", answer: "Yes. Jetco handles plan filing and permits with the NYC Department of Buildings on your behalf." },
      { question: "Do you work on both residential and commercial projects?", answer: "Yes — brownstones, condos, multifamily, retail, restaurants, and light-industrial projects are all within our scope." },
      { question: "How do you price a contracting job?", answer: "We provide a lump-sum quote for defined scopes and time-and-materials pricing for change orders. All quotes are itemized." },
      { question: "Do you carry insurance for property owners?", answer: "Yes. We carry general liability, workers' compensation, and disability insurance. We provide certificates of insurance on request." },
    ],
    relatedServices: ["circuit-box-repairs", "residential-and-commercial-electrical-service", "generator-installation"],
  },

  "equipment-assembly": {
    title: "Equipment Assembly",
    subtitle: `Commercial Equipment Wiring & Assembly in ${CITY}`,
    image: "https://images.pexels.com/photos/37717004/pexels-photo-37717004.jpeg",
    overview: "Bringing in new commercial kitchen equipment, lab gear, or office hardware? Jetco assembles, powers, and tests it onsite. We run dedicated circuits, install plugs and disconnects, mount equipment securely, and verify everything operates within manufacturer specs — all in compliance with NYC electrical code.",
    benefits: [
      "Onsite assembly and electrical hookup in a single visit",
      "Dedicated 120V, 208V, and 240V circuits as required by the nameplate",
      "Permits and ConEd notifications for high-draw equipment",
      "Compatible with restaurant, medical, lab, and office equipment",
      "Manufacturer-spec torque, ventilation clearances, and disconnects",
      "Post-install verification with multimeter and amp-clamp test",
    ],
    process: [
      { step: 1, title: "Equipment Review", description: "We review the equipment nameplate, manufacturer install guide, and existing service capacity." },
      { step: 2, title: "Dedicated Circuit Install", description: "We pull a new circuit from the panel sized for the equipment's full-load amperage plus NEC headroom." },
      { step: 3, title: "Assembly & Hookup", description: "We mount, level, and wire the equipment to disconnects and receptacles per the install manual." },
      { step: 4, title: "Power-Up & Verification", description: "We energize the equipment, verify voltage and current, and confirm safety interlocks." },
    ],
    faqs: [
      { question: "What kinds of equipment do you assemble?", answer: "Commercial ovens, refrigeration units, espresso machines, medical autoclaves, lab analyzers, server rack equipment, and large office hardware like AV walls." },
      { question: "Do I need a dedicated circuit?", answer: "Almost any equipment over 1,200 watts continuous load should be on a dedicated circuit. We size and install it per the nameplate." },
      { question: "Will you coordinate with the equipment vendor?", answer: "Yes — we regularly coordinate directly with vendors and rigging companies to minimize downtime." },
      { question: "How fast can you respond?", answer: "Most assemblies are scheduled within 48 hours; emergency same-day service is available." },
    ],
    relatedServices: ["electrical-contracting", "residential-and-commercial-electrical-service", "home-setup"],
  },

  "ev-charger-installation": {
    title: "EV Charger Installation",
    subtitle: `Tesla Wall Connector & Level 2 EV Charger Installation in ${CITY}`,
    image: "https://customer-assets-lqy194kg.emergentagent.net/job_f0006312-c8b3-4027-b89c-f2f3fb76e672/artifacts/609a86fa7ac246a7_20260806_120906.webp",
    overview: "Charge your EV at home with a permitted, code-compliant Level 2 charger installation. Jetco is a Tesla-recommended installer with hundreds of Wall Connectors, ChargePoint, JuiceBox, and Wallbox installs across Brooklyn, Queens, and Manhattan. Federal tax credits up to $1,000 still available.",
    benefits: [
      "Tesla-recommended installer",
      "Up to 44 miles of range per hour with proper circuit sizing",
      "Federal 30C tax credit up to $1,000 for qualified installs",
      "Full permitting with NYC DOB and ConEdison coordination",
      "Load calculation included — no surprise panel upgrades",
      "Clean conduit runs (interior or exterior) sized for future EVs",
    ],
    process: [
      { step: 1, title: "Free Site Visit", description: "We inspect your panel, measure cable run to the parking location, and confirm load capacity." },
      { step: 2, title: "Permit & Plan", description: "We file the electrical permit and order any service upgrade if needed." },
      { step: 3, title: "Installation Day", description: "Dedicated 40A or 60A circuit, hardwired or NEMA 14-50, mounted at proper height with strain relief." },
      { step: 4, title: "Inspection & Activation", description: "We walk you through the app pairing, current limits, and schedule the DOB inspection." },
    ],
    faqs: [
      { question: "How long does a Tesla Wall Connector install take?", answer: "Most installs are completed in 4–6 hours. Complex runs through finished walls or a panel upgrade can extend that to a full day." },
      { question: "What does EV charger installation cost in Brooklyn?", answer: "Typical pricing ranges $1,200–$2,500 turnkey: charger, copper wire, conduit, breaker, permit, and inspection. A panel upgrade, if needed, runs $2,500–$4,500." },
      { question: "Do I need a panel upgrade for a Level 2 charger?", answer: "Not always. Many 100A and most 200A panels can accept a 40–60A charger after a load calculation. We tell you upfront." },
      { question: "Is the 30C federal tax credit still available?", answer: "Yes — for residential installs in qualifying census tracts, the 30C tax credit covers 30% of cost up to $1,000. We provide receipts and the part numbers your CPA needs." },
    ],
    relatedServices: ["smart-home-system-installation", "circuit-box-repairs", "outdoor-electrical-services"],
  },

  "furniture-assembly": {
    title: "Furniture Assembly",
    subtitle: `Professional Furniture Assembly & Mounting Service in ${CITY}`,
    image: "https://images.unsplash.com/photo-1645651964715-d200ce0939cc",
    overview: "Whether it's a new home office, IKEA wardrobe, or built-in entertainment center, our team assembles and securely mounts furniture across NYC. We bring power tools, anchors rated for brick and gypsum, and the patience to follow the instructions. When the assembly involves wiring — like an under-cabinet light or a motorized standing desk — our electricians handle that part too.",
    benefits: [
      "Same-day or next-day appointments",
      "Power-tool assembly, no missing steps",
      "Wall anchoring rated for NYC brick, plaster, or drywall",
      "Integrated electrical work when needed (motorized desks, lighting)",
      "Packaging removal and proper disposal",
      "Insured, background-checked technicians",
    ],
    process: [
      { step: 1, title: "Quote by Item Count", description: "Send us the product links or model numbers and we send a flat-rate quote within an hour." },
      { step: 2, title: "Scheduled Visit", description: "We arrive in a 2-hour window with all required tools — drills, levels, ratchets, and anchors." },
      { step: 3, title: "Assembly & Mounting", description: "We assemble per the manufacturer instructions and mount to studs or masonry as appropriate." },
      { step: 4, title: "Cleanup & Walkthrough", description: "All packaging is broken down for recycling and we walk you through any moving parts." },
    ],
    faqs: [
      { question: "What furniture brands do you assemble?", answer: "We assemble IKEA, Wayfair, West Elm, CB2, Pottery Barn, Restoration Hardware, Article, Floyd, and most direct-to-consumer brands." },
      { question: "Do you mount TVs and shelves to brick walls?", answer: "Yes — we use sleeve and wedge anchors rated for NYC pre-war masonry and provide a written load rating after install." },
      { question: "How is pricing calculated?", answer: "We charge per item, not per hour. A typical IKEA wardrobe runs $120–$250 assembled and mounted. Bulk discounts apply at 4+ items." },
      { question: "Can you wire a motorized standing desk?", answer: "Yes. Many motorized desks need a dedicated outlet or wall fishing — our electricians handle that as part of the install." },
    ],
    relatedServices: ["equipment-assembly", "home-setup", "recessed-lighting"],
  },

  "generator-installation": {
    title: "Generator Installation",
    subtitle: `Standby & Portable Generator Installation in ${CITY}`,
    image: "https://customer-assets-lqy194kg.emergentagent.net/job_f0006312-c8b3-4027-b89c-f2f3fb76e672/artifacts/a4fbc23c5b97f51a_20260806_182304.webp",
    overview: "Stay powered through Brooklyn storms and ConEd outages. Jetco installs Generac, Kohler, and Briggs & Stratton whole-house standby generators with automatic transfer switches, plus permitted portable-generator inlets and interlocks. All installs include load calculations, gas-line coordination, and NYC DOB filing.",
    benefits: [
      "Whole-house Generac & Kohler standby systems",
      "Automatic transfer switch — power restores in ~10 seconds",
      "Portable generator inlet boxes with code-compliant interlocks",
      "Full permitting and DOB filing included",
      "Coordination with National Grid / ConEd for gas connections",
      "Annual maintenance plans available",
    ],
    process: [
      { step: 1, title: "Load Assessment", description: "We calculate your essential vs. whole-house load to right-size the generator." },
      { step: 2, title: "Permit & Site Prep", description: "We file the electrical and (if required) mechanical permits, and prep the equipment pad." },
      { step: 3, title: "Generator & ATS Install", description: "We set the generator, run gas line coordination, and wire the automatic transfer switch into the service." },
      { step: 4, title: "Commissioning & Walkthrough", description: "Weekly self-test is configured, we run a live transfer drill, and you sign off." },
    ],
    faqs: [
      { question: "How long does a whole-house generator install take?", answer: "From contract to commissioning, typical installs run 3–6 weeks because of NYC permitting and gas-utility scheduling. Actual labor is 2–3 days." },
      { question: "What does a Generac installation cost in Brooklyn?", answer: "A 14kW–22kW whole-house Generac with ATS, pad, gas line, and permits typically runs $9,000–$15,000 installed." },
      { question: "Do I need a permit for a portable generator inlet?", answer: "Yes. NYC requires a permitted interlock kit or transfer switch — back-feeding a panel without one is illegal and unsafe." },
      { question: "Can a generator power my whole house?", answer: "Yes, with proper sizing. A 22kW unit will run a typical 2,500 sq ft Brooklyn home including central air. Smaller homes can use a 14kW unit." },
    ],
    relatedServices: ["circuit-box-repairs", "electrical-contracting", "outdoor-electrical-services"],
  },

  "home-setup": {
    title: "Home Setup",
    subtitle: `Move-In Electrical & Tech Setup Service in ${CITY}`,
    image: "https://images.unsplash.com/photo-1616440347437-b1c73416efc2",
    overview: "Just moved into a new Brooklyn home? Jetco bundles every electrical and low-voltage task you need into one move-in setup visit: TV mounting, internet wiring, USB outlets, dimmers, smart switches, doorbell cameras, and surge protection. One visit, one invoice, no chasing five contractors.",
    benefits: [
      "Single-visit setup — usually under 6 hours",
      "TV and soundbar mounting with concealed wiring",
      "Smart switches, dimmers, and USB-C outlets",
      "WiFi access point and ethernet drop installation",
      "Whole-house surge protection at the panel",
      "Doorbell camera and exterior light upgrades",
    ],
    process: [
      { step: 1, title: "Pre-Move Walkthrough", description: "Send floor-plan photos and we'll build a checklist of every electrical/AV task before you move in." },
      { step: 2, title: "Quote & Schedule", description: "Flat-rate quote per task. We schedule a single visit to knock it all out." },
      { step: 3, title: "Setup Day", description: "Mount, wire, install, and test every item. Old fixtures and packaging removed." },
      { step: 4, title: "Walkthrough & App Pairing", description: "We pair every smart device to your phone and label every breaker." },
    ],
    faqs: [
      { question: "What is a home setup service?", answer: "It's a single-visit package combining electrical, low-voltage, and smart-home tasks for new homeowners or renters. Think of it as 'unpacking, but for your wiring.'" },
      { question: "What does a typical Brooklyn home setup cost?", answer: "Most apartments run $400–$900 for a full setup. Whole houses with TV mounts, smart switches, and AV typically run $1,200–$2,800." },
      { question: "Do you handle internet and AV?", answer: "Yes — ethernet drops, WiFi access point mounting, AV cabling, and TV wall mounts are all included." },
      { question: "Can you do this before I move in?", answer: "Absolutely — most clients prefer it. We coordinate with your building super and lock-box for entry." },
    ],
    relatedServices: ["smart-home-system-installation", "recessed-lighting", "furniture-assembly"],
  },

  "outdoor-electrical-services": {
    title: "Outdoor Electrical Services",
    subtitle: `Exterior Wiring, Landscape Lighting & Outdoor Outlets in ${CITY}`,
    image: "https://images.pexels.com/photos/34581643/pexels-photo-34581643.jpeg",
    overview: "Patios, backyards, and brownstone stoops need weatherproof, code-compliant wiring. Jetco installs landscape lighting, GFCI outdoor outlets, hot-tub disconnects, exterior security cameras, low-voltage path lighting, and dedicated circuits for sheds — all rated for NYC weather and inspected for safety.",
    benefits: [
      "Weatherproof in-use covers and GFCI/AFCI protection",
      "Landscape and low-voltage path lighting design",
      "Hot-tub and pool disconnects with proper bonding",
      "Exterior receptacles and dedicated circuits for sheds",
      "Trenching and conduit for buried service runs",
      "Holiday-light outlets with timers and dusk-to-dawn controls",
    ],
    process: [
      { step: 1, title: "Site Visit & Design", description: "We walk the property, identify power sources, and design the layout (especially for lighting)." },
      { step: 2, title: "Materials & Permit", description: "All wire, conduit, and fixtures are spec'd for outdoor/wet locations. Permit filed when required." },
      { step: 3, title: "Installation", description: "Trenching, conduit, fixture mounting, transformer placement, and GFCI testing." },
      { step: 4, title: "Programming & Walkthrough", description: "Timers, photocells, and app pairing for smart outdoor lighting are configured." },
    ],
    faqs: [
      { question: "Do outdoor outlets in Brooklyn need GFCI?", answer: "Yes — every outdoor receptacle must be GFCI-protected per NEC 210.8 and use a weather-resistant 'in-use' cover." },
      { question: "How much does outdoor electrical work cost?", answer: "A single GFCI outdoor outlet runs $250–$450. A full landscape-lighting design with 8–10 fixtures and a transformer typically runs $1,800–$3,500." },
      { question: "Can you install a hot tub circuit?", answer: "Yes — we install the dedicated 50A or 60A disconnect, GFCI breaker, and bonding required by code, and pull the NYC DOB permit." },
      { question: "Will buried wiring damage my garden?", answer: "We use targeted trenching (typically 12–18 inches deep) and restore landscaping after backfill. Most projects leave no visible trace within a season." },
    ],
    relatedServices: ["recessed-lighting", "generator-installation", "smart-home-system-installation"],
  },

  "recessed-lighting": {
    title: "Recessed Lighting",
    subtitle: `LED Recessed Light Installation & Retrofits in ${CITY}`,
    image: "https://images.unsplash.com/photo-1649083048770-82e8ffd80431",
    overview: "Modernize any room with energy-efficient LED recessed lighting. Jetco installs new construction cans, IC-rated retrofits, dimmer-compatible drivers, and tunable-white fixtures across Brooklyn brownstones, condos, and commercial spaces. Most installs are completed in a single day with minimal patching.",
    benefits: [
      "Energy-efficient LED — 85% less power than incandescent",
      "Dimmer-compatible drivers (Lutron Caséta and Diva tested)",
      "Tunable-white (2700K–5000K) options available",
      "Wafer-style fixtures for low-clearance ceilings",
      "IC-rated for insulation contact in older NYC homes",
      "Minimal ceiling patching — most cuts are clean and reusable",
    ],
    process: [
      { step: 1, title: "Lighting Plan", description: "We walk the room and design a layout per IES spacing guidelines for even illumination." },
      { step: 2, title: "Fixture & Dimmer Selection", description: "We help you choose color temperature, beam angle, and dimmer compatibility." },
      { step: 3, title: "Cut, Wire, Install", description: "Clean ceiling cuts, wiring back to the switch leg, and fixture installation." },
      { step: 4, title: "Dimmer Programming", description: "We configure dimmer min/max levels and demonstrate scenes for tunable-white setups." },
    ],
    faqs: [
      { question: "How many recessed lights do I need per room?", answer: "A common rule is one 4-inch can per 16 square feet of ceiling, but we tailor the layout to ceiling height and use. A 12'×14' living room typically uses 6 cans." },
      { question: "What does recessed lighting cost in Brooklyn?", answer: "Wafer LED retrofits in an accessible ceiling run $135–$185 per fixture installed. New-construction cans in older plaster ceilings run $185–$275 each because of patching." },
      { question: "Can you install recessed lights in a plaster ceiling?", answer: "Yes. We use specialized wafer fixtures designed for shallow plaster cavities and minimize patching." },
      { question: "Are LED recessed lights dimmable?", answer: "Most are, but you need a dimmer rated for LED loads. We install Lutron Caséta or Diva LED+ dimmers tested for the fixture you choose." },
    ],
    relatedServices: ["smart-home-system-installation", "home-setup", "outdoor-electrical-services"],
  },

  "christmas-light-installation": {
    title: "Christmas Light Installation",
    subtitle: `Professional Christmas Light Design, Installation & Takedown in ${CITY} and Surrounding Areas`,
    image: "https://customer-assets-lqy194kg.emergentagent.net/job_f0006312-c8b3-4027-b89c-f2f3fb76e672/artifacts/559086851551c28f_bright-home-lights-permanent-led-lifestyle-hero_f6b78277-0d7b-488b-9dff-66462f8a382f.png",
    overview: "Skip the ladder and the tangled strands. Jetco Electrical Contractors designs, installs, maintains, and takes down commercial-grade Christmas light displays for homes and businesses across Brooklyn, Queens, Staten Island, and lower Manhattan. Every install uses energy-efficient LED strands cut to length for your roofline, trees, and railings — powered through weatherproof, GFCI-protected outlets installed by our electricians.",
    benefits: [
      "Custom roofline, tree, and railing design — not off-the-shelf",
      "Commercial-grade LED strands cut to length (no ugly extensions)",
      "Weatherproof GFCI-protected plug points installed to NYC code",
      "Dusk-to-dawn timer or smart-app control included",
      "Maintenance visits throughout the season if a bulb fails",
      "Professional takedown and labeled storage in January",
      "Fully insured and bonded — no uninsured ladder work",
    ],
    process: [
      { step: 1, title: "Design Consultation", description: "We walk your property, listen to the look you want (warm white, multicolor, animated), and sketch a plan with strand counts and plug locations." },
      { step: 2, title: "Materials & Quote", description: "You receive a line-item quote: strand type, total linear footage, timer or smart controller, and installation + takedown labor. No surprise fees." },
      { step: 3, title: "Install Day", description: "Our insured crew mounts strands with roofline clips (no roof damage), secures tree wraps, and connects everything to a code-compliant GFCI outlet." },
      { step: 4, title: "Season Maintenance", description: "If a strand or bulb fails, we return within 48 hours — included in the quote. Everything stays bright through New Year's." },
      { step: 5, title: "Takedown & Storage", description: "First week of January, we remove everything carefully, label it, and store it for next year — or hand it to you boxed." },
    ],
    faqs: [
      { question: "Do you install my lights or do I need to buy new ones?", answer: "Either. We stock commercial-grade C9 and mini-LED strands cut to your roofline, or we can install lights you already own if they're in safe working condition." },
      { question: "How much does Christmas light installation cost in Brooklyn?", answer: "Typical pricing: $400–$750 for a brownstone roofline, $800–$1,600 for a full townhouse with trees and railings, and $1,500–$4,000+ for larger single-family homes with multi-roofline displays. Pricing includes installation, in-season maintenance, and January takedown." },
      { question: "When should I book Christmas light installation?", answer: "Book by mid-November for a pre-Thanksgiving install. We take bookings through early December but availability fills fast. Takedown is scheduled automatically." },
      { question: "Do you install on brownstones with no roof access?", answer: "Yes. Most Brooklyn brownstones require ladder work and railing-mounted installs. Our crew is OSHA-trained for ladder work up to 32 feet and fully insured." },
      { question: "What areas do you serve?", answer: "Brooklyn (all neighborhoods), Queens (Astoria, Long Island City, Forest Hills, Rego Park), Staten Island (north shore), and lower Manhattan below 59th Street." },
      { question: "Will installation damage my roof or siding?", answer: "No. We use plastic roofline clips that attach to shingles and gutters without screws, nails, or adhesive. Zero damage, zero holes." },
      { question: "Do you offer smart controls?", answer: "Yes. We can set your display on a dusk-to-dawn photocell timer (included) or upgrade to a smart WiFi controller so you can turn lights on/off from your phone and schedule them around holiday parties." },
    ],
    relatedServices: ["outdoor-electrical-services", "recessed-lighting", "home-setup"],
  },

  "residential-and-commercial-electrical-service": {
    title: "Residential and Commercial Electrical Service",
    subtitle: `Full-Service Residential & Commercial Electrical Contractor in ${CITY}`,
    image: "https://images.pexels.com/photos/8853472/pexels-photo-8853472.jpeg",
    overview: "Whether you own a Park Slope brownstone or a Sunset Park warehouse, Jetco provides fully-insured electrical service end-to-end. From a leaking outlet to a complete tenant build-out, the same team handles diagnosis, permitting, installation, and inspection — keeping accountability simple.",
    benefits: [
      "One contractor for homes, condos, retail, restaurants, and offices",
      "DOB-filed, code-compliant work",
      "Service upgrades, sub-panels, and three-phase service",
      "Tenant improvements with minimal downtime",
      "Preventive-maintenance contracts for commercial clients",
      "Emergency 24/7 service with documented response times",
    ],
    process: [
      { step: 1, title: "Free Consultation", description: "Phone or onsite — we scope your needs and outline a fixed-fee or T&M path forward." },
      { step: 2, title: "Permit & Plan", description: "All work requiring permits is filed with NYC DOB; we manage inspection scheduling." },
      { step: 3, title: "Execution", description: "Clean, label-everything workmanship from rough-in to trim-out. Daily site cleanup on commercial jobs." },
      { step: 4, title: "Documentation & Warranty", description: "You receive panel schedules, as-built drawings on commercial jobs, and our 1-year workmanship warranty." },
    ],
    faqs: [
      { question: "Do you take small residential calls?", answer: "Yes — single outlets, light installations, and minor troubleshooting are all welcomed. There's no minimum project size." },
      { question: "Can you serve as our building's preferred contractor?", answer: "Yes — we maintain preventive-maintenance contracts with multifamily and commercial clients across NYC. We can provide referrals on request." },
      { question: "Do you provide free estimates?", answer: "Free phone consults are standard. Onsite estimates are free for residential work and quoted for complex commercial scopes." },
      { question: "What's your response time for emergencies?", answer: "Within 60 minutes for live-safety calls (no power to refrigerator, sparking outlet, smoke). Within 4 hours for non-emergency same-day calls." },
    ],
    relatedServices: ["electrical-contracting", "circuit-box-repairs", "generator-installation"],
  },

  "smart-home-system-installation": {
    title: "Smart Home System Installation",
    subtitle: `Smart Switches, Sensors & Automation Installation in ${CITY}`,
    image: "https://customer-assets-lqy194kg.emergentagent.net/job_f0006312-c8b3-4027-b89c-f2f3fb76e672/artifacts/97cbc4bc44c788ca_20260806_115908.webp",
    overview: "Turn your Brooklyn home into a smart home that actually works. We install and configure Lutron, Caséta, Leviton, Kasa, and Apple HomeKit / Google Home / Amazon Alexa ecosystems — and we add the neutral wires older NYC homes are missing. One visit, one app, one team that picks up the phone when you need help.",
    benefits: [
      "Whole-home or room-by-room installations",
      "Neutral-wire retrofits for pre-war Brooklyn homes",
      "Apple HomeKit, Google Home, and Alexa-compatible setups",
      "Lutron Caséta certified installer",
      "Scene programming and 'goodnight' / 'morning' automations",
      "Family training on the app — no leftover questions",
    ],
    process: [
      { step: 1, title: "Discovery Call", description: "We learn how you live (kids, pets, schedule) and design a smart layout around those habits." },
      { step: 2, title: "Wiring Prep", description: "We pull neutral wires where missing and confirm switch boxes have adequate depth." },
      { step: 3, title: "Device Installation", description: "Smart switches, dimmers, sensors, and hubs are installed and labeled." },
      { step: 4, title: "Scene Programming & Training", description: "We build scenes (dinner, movie, bedtime), connect everything to your ecosystem, and walk the family through the app." },
    ],
    faqs: [
      { question: "Will smart switches work in an older Brooklyn home?", answer: "Almost always — but many pre-war switch boxes lack a neutral wire. We can pull a neutral or recommend battery-free Lutron Caséta devices that don't require one." },
      { question: "What smart home platform should I choose?", answer: "If you're an iPhone household, HomeKit. If you live in Google's ecosystem, Google Home. If you have lots of Echo speakers, Alexa. We'll match the platform to your hardware." },
      { question: "What does a smart home installation cost?", answer: "Single-room installs run $400–$900. Whole-home Lutron Caséta or Leviton conversions typically run $2,500–$8,000 depending on switch count." },
      { question: "Can you integrate cameras, locks, and thermostats?", answer: "Yes — we install and integrate Ring, Nest, Ecobee, Schlage, Yale, August, and many other devices into a single dashboard." },
    ],
    relatedServices: ["recessed-lighting", "ev-charger-installation", "home-setup"],
  },
};

// Article content uses a simple markdown subset already supported by BlogPost.jsx
// (## H2, ### H3, #### H4, "- " bullets, blank line for paragraph break).
export const additionalBlogPosts = [
  {
    id: 101,
    slug: "circuit-box-repair-brooklyn-when-to-call",
    title: "Circuit Box Repair in Brooklyn: 7 Signs You Need an Electrician Now",
    excerpt: "A failing circuit box is the #1 cause of residential electrical fires. Learn the seven warning signs Brooklyn homeowners should never ignore and what a professional repair actually costs.",
    category: "Electrical Safety",
    author: "Jetco Electrical Contractors",
    date: "December 1, 2025",
    readTime: "7 min read",
    image: "https://images.pexels.com/photos/32497160/pexels-photo-32497160.jpeg",
    content: `Your circuit box — also called a breaker panel, fuse box, or load center — is the heart of your home's electrical system. When it starts to fail, the warning signs are usually subtle until they aren't. Here's what to look for, when to act, and what to expect from a professional circuit box repair in Brooklyn.

## 1. Breakers Trip Repeatedly on the Same Circuit

A breaker that trips occasionally is doing its job. A breaker that trips every time you run the microwave plus the toaster is telling you the circuit is overloaded — or worse, the breaker itself is failing. Repeated thermal cycling weakens the internal mechanism, and at some point the breaker won't trip when it should.

## 2. The Panel Feels Warm or Hums

A panel should sit at ambient room temperature. Warmth — especially around the main lugs or a specific breaker — indicates a loose connection generating resistance heat. Buzzing or humming is a sign of arcing inside the panel and is an immediate fire risk.

## 3. You Have a Federal Pacific or Zinsco Panel

If your panel is labeled Federal Pacific (FPE) Stab-Lok or Zinsco, replace it. These panels were installed widely from the 1950s through the 1980s and have documented failure rates as high as one in four breakers not tripping under fault conditions. No repair, retorque, or insurance carrier can make them safe.

## 4. Visible Rust, Corrosion, or Water Stains

Brooklyn basements flood. Brownstone vestibules leak. Any moisture inside the panel oxidizes the bus bars and lugs, increasing resistance and accelerating failure. If you see green corrosion, rust streaks, or water stains, get the panel inspected immediately.

## 5. Burning Smell or Scorch Marks

A faint plastic burning smell near the panel is a fire-in-waiting. Look for scorching around individual breakers — a sign the breaker has arced internally. Shut off the main and call a professional electrician the same day.

## 6. Double-Tapped Breakers

Two wires under a single breaker lug — unless the breaker is specifically rated for it — is a code violation and a common cause of loose-connection fires. Brooklyn homes that have been added onto, especially with a basement apartment retrofit, are prime suspects.

## 7. The Panel Is More Than 30 Years Old

Modern panels are rated for 25–40 years of service. Older Pushmatic, Bulldog, and early GE panels are well past their design life. Even if everything works today, a planned upgrade is far cheaper than an emergency replacement.

## What Does a Professional Circuit Box Repair Cost in Brooklyn?

| Repair | Typical Cost |
| --- | --- |
| Single breaker replacement | $180–$350 |
| Bus-bar repair or retorque | $400–$900 |
| Sub-panel addition | $1,200–$2,400 |
| Full panel replacement (100A → 200A) | $2,800–$4,800 |

All Jetco repairs include free diagnostic, written estimate, NYC DOB filing where required, and a 1-year workmanship warranty.

## What to Do Right Now

1. Stop using the affected circuit if breakers are tripping repeatedly.
2. Don't reset a breaker more than twice — it's telling you something.
3. Schedule a professional electrician for a thermal-scan inspection.
4. Document the date and what you noticed (photos help).

Call Jetco Electrical Contractors at (667) 455-6368 for same-day circuit box inspection across Brooklyn, Queens, and Manhattan.`,
  },

  {
    id: 102,
    slug: "hire-electrical-contractor-brooklyn-2025-guide",
    title: "How to Hire an Electrical Contractor in Brooklyn (2025 Guide)",
    excerpt: "Hiring an electrical contractor in NYC is more complicated than hiring a handyman. Here's exactly what to verify, what to ask, and what red flags should send you running.",
    category: "Hiring Guide",
    author: "Jetco Electrical Contractors",
    date: "December 1, 2025",
    readTime: "8 min read",
    image: "https://images.pexels.com/photos/5667685/pexels-photo-5667685.jpeg",
    content: `Most Brooklyn homeowners only hire an electrical contractor a few times in their lifetime — usually under pressure, often during a renovation, and rarely with much time to compare. This guide walks you through how to vet a contractor properly so you don't end up with unpermitted work, voided insurance, or worse.

## What "Electrical Contractor" Actually Means in NYC

A qualified New York City electrical contractor files electrical plans and permits with the NYC Department of Buildings (DOB), carries proper insurance, and puts every job in writing.

If a contractor can't explain who files the permit and how inspections are scheduled — move on.

## Verify These Five Things Before Signing Anything

### 1. A Track Record of Closed-Out Permits

Ask for examples of recent jobs where the contractor filed and closed out a DOB permit for work similar to yours.

### 2. Certificates of Insurance Naming You as Additional Insured

A real contractor carries general liability ($1M minimum), workers' compensation, and disability insurance. Ask for a Certificate of Insurance (COI) and verify your address is listed. If a worker falls off your stoop without WC coverage, you become the employer of record.

### 3. Written Scope of Work and Itemized Estimate

Vague estimates ("install can lights — $4,000") protect the contractor, not you. A real estimate lists fixture count, wire size, brand, dimmer type, and permit fees. Anything that varies is called out as an allowance.

### 4. Permit Strategy

Ask "What permits will this job require, and who files them?" If the answer is "We don't need permits for that" for work that obviously requires one (panel work, service upgrades, new circuits in finished walls), find another contractor. Unpermitted work voids your homeowner's insurance and shows up at resale.

### 5. References Within the Same Building Type

A contractor who's done 50 brownstones is not necessarily the right fit for a coop or a commercial restaurant build-out. Ask for two references in the same building type as yours and call them.

## Five Questions to Ask on the First Call

1. **Who pulls the permit — you or me?** (Answer: them, every time.)
2. **What's your warranty?** (1 year on workmanship is industry standard.)
3. **Do you carry workers' comp for the crew on my job?** (Yes, with a current COI.)
4. **How do you handle change orders?** (Written, signed, before work begins.)
5. **What payment schedule do you use?** (Typically 25–33% deposit, progress payments, 10% holdback until DOB sign-off.)

## Three Red Flags

- **Cash-only or no written contract.** A reputable contractor wants a paper trail as much as you do.
- **A bid 30% below all other bids.** Either the contractor is uninsured, planning to skip permits, or planning to come back with change orders. None are good outcomes.
- **Reluctance to file a permit.** "We can do it without a permit and save you the filing fee" is the single most common path to unpermitted work that hurts you at resale.

## What Quality Electrical Contracting Actually Costs in Brooklyn

| Project | Typical Range |
| --- | --- |
| 200A service upgrade | $3,500–$5,500 |
| Whole-house rewire (brownstone) | $18,000–$40,000 |
| Tenant build-out (1,500 sq ft retail) | $25,000–$60,000 |
| EV charger install + permit | $1,200–$2,500 |
| Whole-home Lutron Caséta install | $2,500–$8,000 |

These are honest numbers from active Brooklyn jobs. Significantly lower bids almost always involve unpermitted work or thin wire that won't pass inspection.

## How Jetco Approaches a New Job

We start with a free phone consult, follow with an onsite walkthrough, and deliver an itemized written estimate within 48 hours. Every job requiring DOB filing is filed with the city, and we provide COIs naming you as additional insured before the first day of work.

Call (667) 455-6368 or use our [contact form](/#contact) to schedule a free consultation.`,
  },

  {
    id: 103,
    slug: "commercial-equipment-electrical-hookup-nyc",
    title: "Commercial Equipment Electrical Hookup in NYC: What Restaurant & Lab Owners Need to Know",
    excerpt: "Powering up commercial equipment in NYC requires permits, dedicated circuits, and ConEd coordination most vendors won't handle for you. Here's the checklist.",
    category: "Commercial",
    author: "Jetco Electrical Contractors",
    date: "December 2, 2025",
    readTime: "7 min read",
    image: "https://images.pexels.com/photos/37717004/pexels-photo-37717004.jpeg",
    content: `Bringing in a new combi oven, autoclave, espresso machine, or rack of medical equipment is rarely a "plug it in and go" affair in NYC. The equipment vendor delivers it. They don't wire it. That's where a professional electrical contractor comes in — and getting the hookup wrong can cost you weeks of downtime and a failed health-department or DOB inspection.

## Read the Nameplate First — Then Build the Circuit

Every piece of commercial equipment has a nameplate. The numbers that matter:

- **Voltage** — 120V, 208V single-phase, 208V three-phase, 240V, or 480V
- **Amperage (full-load amps, or FLA)** — the steady-state current draw under normal operation
- **Inrush or locked-rotor amperage** — what it pulls at startup (often 3–6× FLA)
- **Phase requirement** — single-phase or three-phase
- **MOCP (maximum overcurrent protection)** — the largest breaker the manufacturer permits

The circuit must be sized to handle FLA at 125% (NEC 210.19) and the breaker must not exceed the MOCP. Get either wrong and the equipment manufacturer voids the warranty.

## Dedicated Circuits Are Almost Always Required

Anything continuous-load over 1,200 watts (such as commercial refrigeration, ovens, autoclaves, or rack-mounted compute) belongs on a dedicated circuit. Sharing a circuit causes voltage drop that shortens motor life and trips breakers under normal use.

For Brooklyn restaurant kitchens, here's a typical circuit map:

- **Combi oven:** 208V/3Ø, 60A breaker, dedicated
- **Walk-in compressor:** 208V/3Ø, 30A breaker, dedicated
- **Reach-in refrigeration:** 120V, 20A, dedicated per unit
- **Espresso machine (3-group):** 240V/1Ø, 30A breaker, dedicated
- **Ice machine:** 120V, 20A, dedicated
- **Salamander broiler:** 240V/1Ø, 30A, dedicated

## Permits Are Not Optional

Adding circuits, sub-panels, or three-phase service in NYC requires an electrical permit filed with the DOB. Health department signoff on a restaurant build-out depends on having that permit closed out. Skipping it means redoing the work twice — once illegally, once for the inspector.

## When You Need a Service Upgrade

A typical Brooklyn 200A single-phase residential service cannot run a commercial kitchen. Most restaurant build-outs require:

- 400A single-phase service for small QSR locations
- 400A three-phase service for full-service restaurants with combi ovens
- 600A+ three-phase for ghost kitchens and commissaries

The lead time from ConEd for a new commercial service is 6–14 weeks. If you're building out a new lease, file early.

## Equipment Assembly + Electrical Hookup in One Visit

Jetco coordinates with your equipment vendor or rigging company to assemble, mount, and power up in a single visit. We bring:

- Dedicated circuits pulled from the panel
- Manufacturer-spec disconnects (NEMA 1, 3R, or 4X as the location requires)
- Proper conduit (EMT or liquid-tight where required)
- A multimeter and amp clamp to verify operation under load
- Documentation for your DOB and health-department inspections

## Common Mistakes We See

1. **Using a 50A breaker because the receptacle is 50A.** The receptacle has nothing to do with circuit sizing — the equipment FLA and conductor ampacity do.
2. **Sharing a neutral on a three-phase circuit.** Shared neutrals on a three-phase MWBC are dangerous and code-violating in most NYC contexts.
3. **Installing a NEMA 14-50 when the equipment needs hardwired connection.** Many commercial appliances require a permanent hardwired connection per the install manual.
4. **Skipping the GFCI requirement on commercial kitchens.** NEC 210.8(B) requires GFCI on most commercial kitchen receptacles. Health inspectors check.

## Call Before You Buy

The most expensive mistake we see is restaurants and labs buying equipment without checking whether the existing service can support it. Send us a list of nameplate specs before you sign the purchase order and we'll tell you within 24 hours whether your panel and service can support it — or what upgrade will be required.

Call Jetco at (667) 455-6368 for a free commercial equipment assessment in Brooklyn, Queens, or Manhattan.`,
  },

  {
    id: 104,
    slug: "ev-charger-installation-cost-brooklyn-2025",
    title: "EV Charger Installation Cost in Brooklyn: 2025 Pricing Breakdown",
    excerpt: "What does it really cost to install a Tesla Wall Connector or Level 2 EV charger in a Brooklyn home? Detailed pricing, tax credits, and panel upgrade guidance.",
    category: "EV Chargers",
    author: "Jetco Electrical Contractors",
    date: "December 2, 2025",
    readTime: "9 min read",
    image: "https://images.pexels.com/photos/27355833/pexels-photo-27355833.jpeg",
    content: `Charging your EV at home is dramatically cheaper, faster, and more convenient than relying on public chargers. But what does it really cost to install a Tesla Wall Connector or other Level 2 charger in a Brooklyn home? Here's the 2025 breakdown — every line item, plus the tax credits that can knock $1,000 off the bill.

## Typical Brooklyn EV Charger Install: $1,200 – $2,500 Turnkey

A "typical" install — meaning your panel has spare capacity, the run from panel to parking is under 30 feet, and no service upgrade is required — falls in this range. Here's what's included:

| Line Item | Typical Cost |
| --- | --- |
| Level 2 charger hardware (Tesla, ChargePoint, Wallbox) | $400–$700 |
| Copper wire and conduit | $200–$400 |
| 40A or 60A breaker | $50–$120 |
| Labor (4–6 hours) | $450–$900 |
| NYC DOB permit and inspection | $150–$250 |
| **Total** | **$1,200–$2,500** |

## When the Number Goes Up

### Long Cable Runs

Every 10 feet of cable between the panel and the charger adds roughly $80–$140 in materials and labor. A garage at the back of a 60-foot lot can add $400+.

### Panel Upgrade Required

If your panel doesn't have spare capacity, a 100A → 200A service upgrade runs $2,800–$4,800 separately. A load calculation is the only way to know for sure — and it should be free with any honest installer.

### Concrete or Brick Penetration

Running conduit through Brooklyn brownstone brick adds 1–2 hours of labor and roughly $150 in materials. Through a finished plaster wall, expect even more.

### NEMA 14-50 vs. Hardwired

A NEMA 14-50 outlet is slightly cheaper but limits you to 32A continuous (40A circuit). A hardwired charger can be sized to 48A continuous (60A circuit) and is more reliable long-term. We recommend hardwiring whenever possible.

## Federal & New York Incentives Still Available in 2025

### Federal 30C Tax Credit (Up to $1,000)

If your home is in a qualifying census tract (most of Brooklyn outside the wealthiest zip codes qualifies), the federal 30C Alternative Fuel Vehicle Refueling Property tax credit covers **30% of installation cost up to $1,000**.

Check eligibility using the [DOE 30C census tract tool](https://www.energy.gov/) — we can help during the consult.

### ConEdison EV SmartCharge Brooklyn

ConEd's SmartCharge program pays Brooklyn EV owners $25/month for off-peak charging, plus a $250 enrollment bonus. Free, takes 5 minutes to enroll, and works with most Level 2 chargers.

## What About a Tesla Wall Connector vs. ChargePoint vs. Wallbox?

| Charger | Max Output | Best For |
| --- | --- | --- |
| Tesla Wall Connector (Gen 3) | 48A / 11.5kW | Tesla owners, sleekest design |
| ChargePoint Home Flex | 50A / 12kW | Multi-vehicle households, robust app |
| Wallbox Pulsar Plus | 40A / 9.6kW | Smaller installs, smart-home integration |
| Emporia EV | 48A / 11.5kW | Budget-conscious, energy monitoring built in |

All four are excellent. Pick the one that matches your car and your app preference.

## Charging Speed: What 40A vs. 48A Actually Means

A 40A circuit (32A continuous) delivers about 7.7 kW — roughly 25–30 miles of range per hour. A 60A circuit (48A continuous) delivers 11.5 kW and adds 40–44 miles per hour. For a daily commuter that plugs in every night, 40A is plenty. For households where the car may need a top-up during the day, 48A is worth the extra ~$200.

## The Hidden Cost of Skipping the Permit

We sometimes hear, "Can we skip the permit and save the fee?" The answer is no, for three reasons:

1. **Insurance.** If an unpermitted EV charger contributes to a house fire, your homeowner's insurance can deny the claim.
2. **Resale.** Unpermitted electrical work shows up in title searches and must be remediated at resale — usually at the seller's expense.
3. **Manufacturer warranty.** Tesla and most others require professional installation with permits and inspection for warranty coverage.

The NYC DOB permit fee is typically $150–$250. Skipping it costs far more than it saves.

## Our EV Install Process

1. **Free site visit** — we measure, run a load calculation, and confirm panel capacity.
2. **Permit filing** — we file with NYC DOB and schedule the rough/final inspections.
3. **Install day** — usually 4–6 hours, single trip.
4. **Activation & training** — we walk you through app pairing, current limits, and scheduled charging.

Call (667) 455-6368 or [request a free estimate](/#contact) for EV charger installation anywhere in Brooklyn, Queens, or Manhattan.`,
  },

  {
    id: 105,
    slug: "professional-furniture-assembly-brooklyn-when-worth-it",
    title: "Professional Furniture Assembly in Brooklyn: When It's Worth Hiring Out",
    excerpt: "Spending a Saturday with an Allen key and unclear instructions? Here's when it makes sense to pay a pro for furniture assembly — and how much it actually costs in NYC.",
    category: "Home Services",
    author: "Jetco Electrical Contractors",
    date: "December 3, 2025",
    readTime: "6 min read",
    image: "https://images.unsplash.com/photo-1645651964715-d200ce0939cc",
    content: `IKEA, Wayfair, West Elm, Article — half of New York furnishes apartments from boxes that arrive flat. The question isn't whether to assemble, it's whether to spend your Saturday doing it or pay someone to do it right the first time. Here's an honest breakdown for Brooklyn renters and homeowners.

## When DIY Makes Sense

- **Single small piece**, like a bookshelf or side table, with clear instructions
- **You enjoy the process** (some people genuinely do — no judgment)
- **You have all the tools**, including a drill, level, stud finder, and patience
- **No wall mounting required** — freestanding items are far more forgiving

## When to Hire a Professional

### Multiple Pieces in One Day

A bedroom set, a home office, or a full living room delivered the same weekend will easily eat 8–12 hours of self-assembly. Professionals do the same work in 3–5 hours because they assemble in parallel.

### Anything That Mounts to a Wall

NYC apartment walls are a hodge-podge of plaster, brick, hollow gypsum, and old lath. Anchoring a 60-pound shelf, a TV, or a tall wardrobe to the wrong substrate can pull a chunk of wall down with the furniture. A pro brings the right anchors for each substrate.

### IKEA PAX Wardrobes, Komplement Drawers, Built-In Look

PAX systems require leveling, securing to the wall (mandatory per IKEA's instructions, often ignored), and aligning multiple frames into a flush row. Done poorly, drawers won't track. Done well, it looks built-in.

### Murphy Beds, Loft Beds, Modular Closets

Anything that swings, supports a person's weight, or attaches to a structure should be assembled by someone who's done it before. The safety margin matters.

### Anything Powered

Motorized standing desks, automated blinds, lighted vanity mirrors, and integrated charging stations involve electrical work. Our electricians handle cable management, dedicated outlets, and wall fishing as part of the assembly visit.

## What Brooklyn Furniture Assembly Actually Costs

| Item | Typical Cost |
| --- | --- |
| IKEA MALM dresser | $80–$120 |
| IKEA PAX wardrobe (single bay) | $120–$180 |
| IKEA PAX wardrobe (3+ bays) | $300–$500 |
| Wayfair / West Elm queen bed frame | $140–$220 |
| Sectional sofa (modular) | $150–$280 |
| Murphy bed (wall-mounted) | $400–$700 |
| TV wall mount (under 65") | $180–$280 |
| TV wall mount (75"+) with concealed cables | $300–$500 |
| Motorized standing desk + outlet install | $250–$450 |

Pricing is per item, not hourly — so adding the dining table doesn't double the bill.

## What's Included in a Professional Visit

- All tools (drills, levels, stud finders, ratchets, torque drivers)
- Anchors rated for the wall type (brick, plaster, drywall, lath)
- Cardboard breakdown and recycling
- Hardware check against the manifest — and ordering replacements if missing
- 30-day workmanship warranty on every assembly

## How to Prepare for Assembly Day

1. **Have the boxes in the room they belong in.** Moving furniture between rooms during assembly doubles the time.
2. **Read the box labels.** IKEA bedroom sets often arrive in 3–6 boxes per piece; make sure all are present before the technician arrives.
3. **Identify the wall studs** if anything is wall-mounted. We'll verify with a stud finder, but knowing your wall type (brick vs. drywall vs. plaster-on-lath) saves time.
4. **Clear floor space.** Assembly needs 30 square feet of staging room per medium piece.

## When Assembly Becomes an Electrical Job

About 30% of our assembly visits turn into combined assembly + electrical jobs:

- Recessed lighting added to a new closet system
- Outlet relocation for a TV mount
- Dedicated circuit for a motorized standing desk
- USB-C outlets installed beside a new nightstand
- Under-cabinet lighting added to assembled shelving

Because we're a full electrical contractor, all of that happens in the same visit — no second contractor required.

Call (667) 455-6368 or [contact us](/#contact) for furniture assembly anywhere in Brooklyn, Queens, or Manhattan.`,
  },

  {
    id: 106,
    slug: "whole-house-generator-installation-brooklyn-guide",
    title: "Whole-House Generator Installation in Brooklyn: Sizing, Costs, and What to Expect",
    excerpt: "ConEd outages, hurricane prep, and basement floods — Brooklyn homeowners are installing standby generators in record numbers. Here's how to size one, what it costs, and what NYC permits require.",
    category: "Generators",
    author: "Jetco Electrical Contractors",
    date: "December 3, 2025",
    readTime: "8 min read",
    image: "https://images.unsplash.com/photo-1612194092957-c182f4de4433",
    content: `Brooklyn homeowners — especially those with sump pumps, finished basements, or family members on medical equipment — are installing whole-house standby generators in record numbers. The question is no longer "Should I get one?" but "What size, what fuel, and what does NYC require?"

## Standby vs. Portable: Pick the Right Tool

### Whole-House Standby Generator

Permanently installed outside, automatically starts within 10 seconds of an outage, runs on natural gas or propane, and powers the entire home (or a chosen subset) until utility power returns.

- **Best for:** Homeowners, sump pumps, central AC, refrigeration, medical equipment
- **Investment:** $9,000–$15,000 installed
- **Lifespan:** 15–20 years with annual maintenance

### Portable Generator + Inlet Box

A gas-powered portable generator (4,000–8,000 watts) connected to your panel through a permitted interlock kit or transfer switch. Manual start, manual fueling, runs essential circuits only.

- **Best for:** Occasional outages, smaller homes, lower budget
- **Investment:** $1,800–$3,200 installed (excluding the generator itself)
- **Lifespan:** Generator 7–10 years, inlet box 20+

## Sizing the Right Generator

The mistake we see most often is undersizing. A 10kW generator can't run central AC plus a sump pump plus a refrigerator simultaneously. Here's a sane sizing guide for Brooklyn homes:

| Home Size | Loads to Cover | Recommended Generator |
| --- | --- | --- |
| 800–1,500 sq ft apartment | Refrigerator, lights, sump | 7–10 kW (portable or compact standby) |
| 1,500–2,500 sq ft brownstone | Above + central AC + microwave | 14 kW standby |
| 2,500–4,000 sq ft single-family | Whole house including dual AC | 22 kW standby |
| 4,000+ sq ft / EV charging during outage | Whole house + EV charging | 24–26 kW standby + load shedding |

A load calculation done by a professional electrician is the only reliable way to size this — guessing usually costs more in the long run.

## Permits, Inspections, and Gas Coordination in NYC

NYC requires:

1. **Electrical permit** filed with the DOB before installation begins
2. **Mechanical or fuel-gas permit** for natural gas connection (often coordinated through the plumber)
3. **National Grid or Con Edison gas service request** for new or upsized gas service if existing line is undersized
4. **DOB rough and final inspections** before commissioning

Total permit-to-commission timeline in Brooklyn is typically 3–6 weeks. The labor itself is only 2–3 days; the rest is permitting and gas coordination.

## Installed Cost Breakdown for a 22kW Generac

| Line Item | Typical Cost |
| --- | --- |
| Generac 22kW Guardian unit | $4,800–$5,800 |
| Automatic transfer switch (200A) | $700–$1,000 |
| Concrete pad and site prep | $300–$700 |
| Gas line extension (50 ft typical) | $1,200–$2,000 |
| Electrical labor and conduit | $1,500–$2,500 |
| Permits and inspections | $400–$700 |
| **Total** | **$8,900–$12,700** |

Larger homes or longer gas runs push toward $15,000.

## Where to Place the Generator

NYC code requires:

- Minimum 18 inches from the building wall
- Minimum 5 feet from any operable window or door
- Minimum 3 feet from property lines (varies by borough — check with your installer)
- A concrete pad or composite manufacturer-approved pad

In dense Brooklyn lots, this is often the single biggest constraint. We'll walk your property during the consult to confirm a viable location before quoting.

## Maintenance and Lifetime Cost

Standby generators need:

- **Annual service** — oil change, filter swap, battery check ($350–$500/year)
- **Weekly self-test** — automatic, runs 12 minutes, drinks ~$2 of gas
- **Battery replacement** — every 4–5 years ($120–$180)
- **Spark plug replacement** — every 200 run hours

Plan on ~$500/year over the unit's lifetime.

## Portable Generator Inlets: The Smart Middle Ground

If a whole-house standby is out of budget, a permitted inlet box with an interlock kit is the next best thing. Cost is $1,800–$3,200, and you can use any 30A or 50A portable generator you already own or borrow during a storm. Crucially, this is the **only legal way to back-feed a panel in NYC** — never use a "suicide cord" or back-feed through an outlet.

## Our Process

1. Free in-home consultation and load calculation
2. Generator sizing and quote within 48 hours
3. Permit filing and gas coordination
4. Installation (2–3 days)
5. Inspection and commissioning with live transfer drill
6. Annual maintenance program optional

Call Jetco at (667) 455-6368 or [contact us](/#contact) for a Brooklyn whole-house generator consultation.`,
  },

  {
    id: 107,
    slug: "new-home-setup-checklist-brooklyn",
    title: "New Home Setup Checklist for Brooklyn: Every Electrical & Tech Item to Handle Before You Unpack",
    excerpt: "Moving into a new Brooklyn home? This single-page checklist covers every electrical, low-voltage, and smart-home task to handle before the boxes arrive.",
    category: "Moving",
    author: "Jetco Electrical Contractors",
    date: "December 4, 2025",
    readTime: "7 min read",
    image: "https://images.unsplash.com/photo-1616440347437-b1c73416efc2",
    content: `The week before you move into a Brooklyn home is the cheapest time to handle every electrical, low-voltage, and smart-home task on your list. Empty walls, easy access, no furniture in the way. Here's the checklist we use with clients who hire us for full move-in setup.

## Before You Move: Walk the Empty Home

### 1. Photograph Every Panel and Sub-Panel

Label any breakers that already have labels. Note any breakers you can't identify — those are the ones we'll trace later. Two minutes now saves two hours of "which circuit is the kitchen?" later.

### 2. Identify Switch Loops Missing Neutrals

Open any switch you'd want to make "smart." If the switch box has no white neutral wire (common in pre-1980 Brooklyn homes), plan on either:

- **Pulling a neutral** during setup (best long-term)
- **Using a no-neutral smart switch** like Lutron Caséta (works around the problem)

### 3. Test Every GFCI Outlet

Press the TEST button on every GFCI in bathrooms, kitchens, garages, and outdoor outlets. Anything older than 10 years should be replaced — they wear out.

### 4. Locate the Sump Pump and Refrigerator Outlets

These should be GFCI-protected and ideally on dedicated circuits. Verify both. A sump pump on a shared circuit that trips during a storm is a flooded basement waiting to happen.

### 5. Note Cable & Coax Termination Points

Find where the cable/internet provider's line enters the house. That's your distribution point for ethernet drops, WiFi access points, and AV racks.

## The Move-In Setup Checklist

### Lighting

- [ ] Replace any incandescent or CFL bulbs with LED equivalents (saves ~$200/year on electricity)
- [ ] Install dimmers in living room, dining room, primary bedroom (LED-compatible)
- [ ] Add under-cabinet kitchen lighting if not present
- [ ] Replace any flickering or buzzing fixtures (sign of failing ballast or driver)
- [ ] Add recessed lighting to dark areas — typical install $135–$185 per fixture

### Outlets

- [ ] Add USB-C combination outlets in the kitchen, primary bedroom, and home office
- [ ] Verify every bathroom has a GFCI within 6 feet of the sink
- [ ] Install a dedicated 20A circuit for any home-office equipment over 1,500W combined
- [ ] Replace any 2-prong outlets in habitable rooms with grounded 3-prong (or GFCI as a code-compliant alternative)

### Safety

- [ ] Replace every smoke and CO detector older than 10 years (mandatory in NYC)
- [ ] Install whole-house surge protection at the panel ($300–$500 installed; protects $10,000+ of electronics)
- [ ] AFCI breakers on bedroom circuits (now required in NYC for new construction; retrofit recommended)
- [ ] GFCI on the laundry, dishwasher, and exterior circuits

### Smart Home

- [ ] Smart thermostat (Ecobee, Nest, or Honeywell T9)
- [ ] Smart switches on primary lighting circuits (start with living room and primary bedroom)
- [ ] Smart smoke/CO sensors connected to your phone (First Alert Onelink, Nest Protect)
- [ ] Smart deadbolt + doorbell camera
- [ ] WiFi access point coverage map — eliminate dead zones now, not after you move in

### AV & Network

- [ ] Mount the TV and conceal cables in-wall
- [ ] Run ethernet to the office, primary TV, and any WiFi access points
- [ ] Install a low-voltage panel in a closet for the router, NVR, and AV gear
- [ ] Soundbar or Sonos installation if planned

### Exterior

- [ ] Test and replace outdoor outlets (must be GFCI + WR + in-use cover)
- [ ] Doorbell camera (Ring, Nest, or wired Eufy)
- [ ] Landscape lighting transformer if you have a front garden or backyard path
- [ ] Holiday-light outlet with dusk-to-dawn photocell

### EV Charging (If Applicable)

- [ ] Verify panel capacity for a 40A or 60A charger
- [ ] Run conduit to the parking location now (cheaper before walls are finished)
- [ ] File the permit (1–2 weeks ahead of install day)
- [ ] Confirm eligibility for the federal 30C tax credit

## Why a Single-Visit Setup Saves Money

Coordinating five different contractors (electrician, AV installer, smart-home tech, networking pro, handyman) means five trip charges, five site walkthroughs, and five chances for someone to be late. A single professional electrical contractor who handles all of the above in one visit typically saves Brooklyn homeowners 25–35% versus piecemeal hiring.

Most of our move-in setups run $1,200–$2,800 depending on house size and scope.

## Our Process

1. Send floor-plan photos and your wish list
2. Receive a line-item quote within 48 hours
3. Schedule a single setup day (4–8 hours typical)
4. Walkthrough, app pairing, and labeled panel at the end

Call Jetco at (667) 455-6368 or [contact us](/#contact) to schedule a Brooklyn move-in setup.`,
  },

  {
    id: 108,
    slug: "outdoor-electrical-outlets-landscape-lighting-brooklyn-code",
    title: "Outdoor Electrical Outlets and Landscape Lighting in Brooklyn: What NYC Code Requires",
    excerpt: "Adding outdoor outlets, hot-tub circuits, or landscape lighting to your Brooklyn home? NYC code is specific about GFCI, weatherproofing, and bonding. Here's what professional electricians install.",
    category: "Outdoor Electrical",
    author: "Jetco Electrical Contractors",
    date: "December 4, 2025",
    readTime: "7 min read",
    image: "https://images.pexels.com/photos/34581643/pexels-photo-34581643.jpeg",
    content: `Brooklyn backyards, brownstone stoops, and rooftop terraces all benefit from proper outdoor electrical. But NYC code is more demanding than for indoor work — and a non-compliant outdoor install can void homeowners insurance, fail at resale, and (most importantly) electrocute someone after a rainstorm.

## The Five Code Requirements for Every Outdoor Outlet

### 1. GFCI Protection (NEC 210.8)

Every receptacle outside the home must be GFCI-protected. This can be a GFCI receptacle itself or a GFCI breaker upstream. The GFCI must trip in under 25 milliseconds at 5 mA of ground-fault current.

### 2. Weather-Resistant (WR) Rated Devices

The receptacle must be marked **WR**. WR devices use corrosion-resistant materials and seals that resist moisture intrusion. Standard indoor receptacles will corrode within 1–2 winters.

### 3. In-Use ("Bubble") Cover

Outdoor outlets need a covered enclosure that closes around a plug while in use, not just when the outlet is empty. These are sometimes called "in-use covers" or "bubble covers." The cover must be marked for the appropriate use.

### 4. AFCI Protection (Some Locations)

For dwelling units, certain new circuits require both AFCI and GFCI protection (dual-function). The NYC electrical code amendment tracks the NEC closely — check during permitting.

### 5. Tamper-Resistant (TR) Rated Devices in Some Locations

In areas accessible to children (rare outdoors but possible for stoop or porch outlets), the receptacle must also be tamper-resistant.

## Landscape Lighting: Low-Voltage Is the Right Answer

Most modern landscape lighting runs on 12V or 24V DC, fed from a low-voltage transformer mounted near an outdoor outlet. Why low-voltage?

- **Safer:** Touching 12V is a non-event; 120V can kill.
- **Cheaper trenching:** Low-voltage cable doesn't require deep burial or conduit in most cases.
- **Easier to add:** Splice in additional fixtures without re-permitting.
- **LED-friendly:** Modern LED landscape fixtures are nearly all low-voltage.

A typical Brooklyn front-yard install: one 300W transformer mounted to the brownstone, 8 pathway fixtures, 2 uplight fixtures highlighting an architectural feature. Installed cost: $1,800–$3,500 including the transformer.

## Hot Tub Circuits: The Most Code-Heavy Outdoor Install

Hot tubs are the most regulated outdoor electrical install. Code requires:

- **50A or 60A dedicated circuit** (per the tub's nameplate)
- **GFCI breaker** (the disconnect itself is GFCI, not a downstream GFCI receptacle)
- **Manual disconnect** within sight of the tub, between 5 and 50 feet from the water
- **Bonding** of all metallic parts (ladder, pump housing, structural reinforcement) to the equipment grounding conductor
- **NYC DOB permit** filed before installation

Skipping bonding is the most common — and most dangerous — code violation we see in hot-tub installs. It's the difference between a safe install and a fatal one.

## Shed and Garage Circuits

Bringing power to a detached shed or garage requires:

- **Direct-burial UF cable** or **PVC conduit underground** at the proper depth (typically 18–24 inches)
- **Equipment grounding conductor** sized for the circuit
- **Separate ground rod** at the outbuilding (current NEC requirement)
- **Sub-panel** if more than a single circuit is needed
- **GFCI protection** at the first device

A typical 20A 120V shed run costs $700–$1,400 depending on trenching distance.

## Holiday Lights: Doing It Right

The single most common outdoor electrical incident in Brooklyn is overloaded holiday-light circuits. To prevent it:

- Install a dedicated 15A or 20A outdoor outlet for holiday lights, ideally with a built-in photocell and timer
- Use LED holiday lights (90% lower current draw than incandescent)
- Don't daisy-chain more than the manufacturer's stated maximum runs
- Inspect cords annually for cracks, splits, or animal damage

## Security Lighting

Modern security lighting integrates motion sensors, photocells, and (increasingly) cameras. Most Brooklyn homes benefit from:

- Two motion-activated floodlights covering the front entry
- One motion-activated floodlight on each side yard
- A continuous low-level photocell light at the rear entry
- A doorbell camera (Ring, Nest, Eufy, Reolink) — wired, not battery, where possible

## Permitting Outdoor Electrical in NYC

The DOB requires permits for:

- Any new outdoor circuit on a dwelling
- Hot tub, pool, or spa wiring
- Service-entrance work or sub-panels
- Solar-related outdoor wiring

It does not generally require permits for:

- Replacing an existing outdoor receptacle in the same box
- Replacing existing landscape lighting fixtures on an existing low-voltage circuit
- Adding low-voltage holiday lights to an existing permitted outdoor receptacle

When in doubt, ask. We file the permit for you whenever required.

## Our Outdoor Electrical Service

We handle everything from a single new outdoor outlet to a full backyard lighting design with smart-home integration. Most projects are completed in a single day, and all are filed with the NYC DOB where required.

Call (667) 455-6368 or [contact us](/#contact) for an outdoor electrical estimate.`,
  },

  {
    id: 109,
    slug: "recessed-lighting-brooklyn-cost-layout-guide",
    title: "Recessed Lighting in Brooklyn: Cost, Layout, and Why LED Wafer Lights Changed Everything",
    excerpt: "Recessed lighting transforms a Brooklyn living room. Here's how to lay it out, what it costs, and why LED wafer lights make even pre-war plaster ceilings retrofittable.",
    category: "Lighting",
    author: "Jetco Electrical Contractors",
    date: "December 5, 2025",
    readTime: "8 min read",
    image: "https://images.unsplash.com/photo-1649083048770-82e8ffd80431",
    content: `Recessed lighting is the single highest-impact electrical upgrade most Brooklyn homeowners make. Done right, it makes a 12-foot ceiling feel architectural and a small room feel larger. Done wrong, it creates harsh shadows and pock-marks the ceiling. Here's what good looks like — plus what it actually costs in 2025.

## Why LED Wafer Lights Changed Recessed Installation

For decades, recessed lighting in Brooklyn meant cutting a 6-inch hole, fishing wire through plaster-and-lath, and dropping in a hot, energy-hungry can. In 2018, LED wafer lights took over.

A wafer light is a thin LED disc with an integrated driver and a junction box that fits inside a typical ceiling cavity. Benefits versus traditional cans:

- **Thinner profile** — fits in shallow cavities (think: above kitchen soffits, in coffered ceilings, in pre-war plaster ceilings with limited depth)
- **No can needed** — the entire fixture sits flush in a 4-inch or 6-inch hole
- **85% less power** — typical wafer is 9–18W vs. 65–75W for an incandescent can
- **20–25 year life** — most fixtures outlast the room you put them in
- **Cooler running** — no insulation contact concerns in older homes (IC-rated by default)

The result: wafer lights are roughly 30% cheaper to install than traditional new-construction cans in retrofit scenarios, and they work in ceilings that wouldn't accept old-style cans at all.

## Layout: How Many and Where

### The Simple Rule

For general ambient lighting at 8-foot ceilings, place 4-inch wafer lights every 4 feet on center, starting 2 feet from each wall.

A 12'×14' Brooklyn living room (168 sq ft) typically uses 6 wafer lights in a 2×3 grid.

### Layered Layout (Better)

Better layout uses three layers:

1. **Ambient** — evenly spaced wafer lights for general illumination
2. **Task** — focused fixtures over kitchens islands, work surfaces, or reading chairs
3. **Accent** — wall washers highlighting art or architectural features

A great living room layout might include:

- 6 ambient wafer lights in a grid
- 2 narrow-beam adjustable accents highlighting a fireplace or art wall
- 1 task pendant or table lamp at a reading chair

### Where Not to Put Recessed Lights

- Directly over a bed (creates glare when lying down)
- Centered over a dining table (the table is fixed — a pendant works better)
- Outside the "useful" zone — too close to walls just lights the corners

## Color Temperature: 2700K vs. 3000K vs. 4000K

| Kelvin | Look | Best For |
| --- | --- | --- |
| 2700K (warm white) | Incandescent-like, cozy | Living rooms, bedrooms, dining rooms |
| 3000K (soft white) | Slightly cooler, still warm | Kitchens, bathrooms, hallways |
| 4000K (neutral) | Hospital-like, alert | Garages, basements, workshops |
| 5000K+ (daylight) | Blue-cast | Generally avoid in residential |

**Tunable-white fixtures** can shift between 2700K–5000K from a single switch — worth the extra $20/fixture in primary living spaces. Lutron's Diva LED+ with tunable-white wafers is a Jetco favorite.

## Dimming: Why You Need an LED-Compatible Dimmer

LED dimmers are not interchangeable. Many older dimmers cause LEDs to flicker, hum, or fail to dim below 30%. Use:

- **Lutron Caséta LED+ (smart, wireless):** Best long-term; works with Apple HomeKit, Google Home, Alexa
- **Lutron Diva LED+ (wired):** Affordable, reliable, no app needed
- **Leviton Decora Smart LED:** Solid alternative if you prefer Decora aesthetics

Match the dimmer to your specific wafer light — fixture manufacturers publish compatibility lists. Don't guess.

## What Recessed Lighting Costs in Brooklyn (2025)

| Scenario | Per Fixture |
| --- | --- |
| 4" wafer retrofit, accessible ceiling, drywall | $135–$160 |
| 6" wafer retrofit, accessible ceiling, drywall | $145–$180 |
| Plaster-and-lath ceiling (more cutting/patching) | $185–$240 |
| New construction can in finished ceiling (rare) | $185–$275 |
| Tunable-white smart fixture | +$20–$45 |
| LED-compatible smart dimmer (per switch) | $80–$140 installed |

A typical 6-fixture Brooklyn living room: **$900–$1,200 installed**, including dimmer.

## How Long Does Installation Take?

A 6-fixture single-room install typically takes 3–5 hours. A whole-floor install (20+ fixtures) takes a full day. Plaster ceilings add 1–2 hours total for patching.

## Permits in NYC

Adding lighting fixtures to an existing circuit (not adding circuits or upgrading the panel) does not always require a permit — but adding a new switch leg or a new dedicated circuit does. We'll tell you during the walkthrough and file the permit if required.

## Avoiding Common Mistakes

1. **Spacing too tight.** 2-foot spacing creates harsh hot spots. Stick to 4-foot grid for ambient.
2. **Wrong color temperature.** 4000K in a living room feels like a hospital. Default to 2700K or 3000K.
3. **Cheap dimmer + LED.** Causes flicker, buzz, and premature LED failure. Always use a verified compatible dimmer.
4. **Skipping the patching budget.** Plaster ceilings need patching — budget $20–$40/cut for the painter even after the electrician is done.

## Our Process

1. **Free in-home design.** We walk the room and propose a layout.
2. **Fixture selection.** We help you pick beam angle, color temperature, and dimmer compatibility.
3. **Single-day install.** Most rooms are completed in 3–5 hours.
4. **Dimmer programming.** We set the dimmer min/max and demonstrate tunable-white scenes.

Call Jetco at (667) 455-6368 or [contact us](/#contact) for a Brooklyn recessed-lighting design.`,
  },

  {
    id: 110,
    slug: "residential-vs-commercial-electrical-brooklyn",
    title: "Residential vs. Commercial Electrical Service in Brooklyn: Why It's the Same Skill, Different Code",
    excerpt: "Brooklyn homeowners often wonder if they need a different electrician for commercial work. Here's the honest answer — and why a contractor who does both gives you better results.",
    category: "Commercial",
    author: "Jetco Electrical Contractors",
    date: "December 5, 2025",
    readTime: "7 min read",
    image: "https://images.pexels.com/photos/8853472/pexels-photo-8853472.jpeg",
    content: `Brooklyn property owners often ask whether they should hire a "commercial" electrician for a retail build-out or a "residential" one for their home. The honest answer is that the underlying electrical skills are identical — but commercial work involves additional code sections, permitting paths, and equipment that residential-only contractors rarely encounter. Here's what separates the two and why a single contractor who handles both ends up being a smarter hire.

## What's Genuinely Different

### Service Sizes and Phases

Residential Brooklyn homes are almost always 120/240V single-phase. Commercial properties are frequently 120/208V three-phase (for restaurants, retail) or 277/480V three-phase (for larger commercial buildings).

Three-phase changes how circuits are sized, how motor loads behave, and how neutrals are routed (no shared neutrals on a multi-wire branch circuit without a common-trip breaker). An electrician who's never wired three-phase will improvise — usually badly.

### Conduit Requirements

NYC commercial work requires conduit (EMT, IMC, or rigid) for almost every exposed circuit. Residential allows Romex (NM-B) cable in most concealed locations. Pulling conduit is a different skill set — bending, threading, supporting per code.

### Permitting Path

Commercial work in NYC frequently requires:

- DOB filing with stamped electrical drawings
- Coordination with ConEdison for service changes
- Compliance with the local NYC Electrical Code amendments (which diverge from the base NEC)
- Health Department coordination for restaurants
- FDNY filings for assembly occupancies

Residential is typically simpler — but still requires DOB filing for panel work, service upgrades, and new circuits.

### Inspection Schedules

Commercial inspections frequently happen mid-construction at rough-in stages. Residential is often a single final inspection. Missing a rough inspection on a commercial job can require opening walls back up — a nightmare.

### Equipment

Commercial loads include three-phase motors, VFDs, large refrigeration compressors, neon signage transformers, kitchen exhaust hoods with interconnected fire suppression, and dedicated server-rack power. Residential rarely encounters any of those.

## What's the Same

- Wire ampacity tables
- Breaker sizing for continuous loads (125%)
- GFCI and AFCI requirements (different application areas, same theory)
- Equipment grounding and bonding
- Permit filing and inspection with the NYC DOB

So an electrician who does both is qualified for both. A residential-only contractor expanding into commercial without the right experience is the risky hire.

## Why Hiring One Contractor for Both Helps You

### Single-Contractor Accountability

If your contractor is qualified and experienced in both, your home renovation and your commercial property maintenance run through the same office. No re-explaining your building. No different invoicing system. No conflicting estimates between sister contractors.

### Better Pricing

Contractors with steady commercial work tend to bid residential work more efficiently. Truck rolls are amortized across more jobs. Materials inventory is broader.

### Continuity for Multi-Use Buildings

Many Brooklyn buildings are mixed-use — retail downstairs, apartments above. A single contractor handling both means common-area work, multi-tenant metering, and life-safety systems are coordinated.

## Common Brooklyn Project Types

### Residential

- Whole-house rewires (most common in brownstones)
- 100A → 200A service upgrades
- EV charger installs
- Kitchen and bath remodels
- Recessed-lighting retrofits
- Smart-home conversions
- Generator installs

### Commercial

- Restaurant build-outs (combi ovens, walk-ins, ventless hoods)
- Coffee shop and bakery service upgrades
- Multi-tenant retail tenant improvements
- Office build-outs (workstation power, AV, conference-room lighting)
- Light industrial maintenance contracts
- Laundromat equipment hookup
- Medical and dental office installs (with specialty grounding)

### Mixed-Use

- Brownstone with ground-floor retail
- Multi-family with shared mechanical loads
- Live-work conversions in converted warehouses

## What Quality Looks Like

A well-run electrical contractor — residential or commercial — has these traits in common:

1. **Permits filed for every job that requires them**, no exceptions
2. **COI on file with the building** before work begins (often required by coops and condos)
3. **Daily site cleanup** on commercial jobs; full cleanup at end of residential jobs
4. **Labeled panels** at the end of every job — no exceptions
5. **Written workmanship warranty**, typically 1 year
6. **24/7 emergency line** that actually picks up
7. **One contact person** from estimate through final inspection

## How Jetco Operates

We handle both residential and commercial work. We carry general liability, workers' compensation, and disability insurance. Most importantly, the same project manager who handles your home is the one available for your commercial property.

Call (667) 455-6368 or [contact us](/#contact) for residential or commercial electrical service in Brooklyn.`,
  },

  {
    id: 111,
    slug: "smart-home-installation-brooklyn-2025-guide",
    title: "Smart Home Installation in Brooklyn: The 2025 Guide for Pre-War and Modern Homes",
    excerpt: "Pre-war Brooklyn brownstones present unique smart-home challenges — missing neutrals, plaster walls, and shallow boxes. Here's how to do it right, what it costs, and what platform to choose.",
    category: "Smart Home",
    author: "Jetco Electrical Contractors",
    date: "December 6, 2025",
    readTime: "9 min read",
    image: "https://images.pexels.com/photos/17828637/pexels-photo-17828637.jpeg",
    content: `Smart-home tech has matured to the point where almost every Brooklyn home can be automated — but pre-war brownstones, walk-up apartments, and converted warehouses each present unique challenges. This guide walks through the practical realities of smart-home installation in Brooklyn, what platform to choose, and what a quality installation actually costs in 2025.

## The Three Biggest Brooklyn Smart-Home Challenges

### 1. Missing Neutrals in Pre-War Switch Boxes

Most pre-war Brooklyn homes were wired without a neutral wire at switch locations — switches simply broke the hot leg. Many "smart" switches need a neutral to power their internal electronics 24/7.

**Three solutions:**

- **Pull a neutral** from the nearest circuit (best long-term, requires opening walls or fishing through ceilings)
- **Use a no-neutral smart switch** — Lutron Caséta works without a neutral; most Decora Smart and Kasa models do not
- **Use a smart bulb instead** — leave the dumb switch in the wall in the always-on position

### 2. Shallow Switch Boxes

Old metal switch boxes in plaster walls are often 1.5 inches deep. Many smart switches need 2+ inches to fit. The fix is a deeper "extra-deep" box or a "switch extender" — straightforward for an electrician, surprise-disaster for a DIYer.

### 3. WiFi Dead Zones in Brick Buildings

Brooklyn brick attenuates WiFi like nothing else. A single router in the front room won't cover a 60-foot brownstone. The solution is a wired mesh — typically Ubiquiti or Eero with ethernet backhaul, with an access point in the front, middle, and rear of the home. This is part of any serious smart-home install.

## Pick the Right Ecosystem

The single biggest decision is which ecosystem to commit to. They are mostly compatible, but day-to-day usability depends on matching the platform to your existing devices.

### Apple HomeKit

- **Best for:** iPhone households, privacy-focused users
- **Strengths:** Local processing, end-to-end encryption, beautiful Home app
- **Weaknesses:** Fewer compatible devices than Google/Alexa
- **Hub:** HomePod mini or Apple TV ($99–$129)

### Google Home

- **Best for:** Android households, deep search/voice usage
- **Strengths:** Best voice recognition, broadest device support
- **Weaknesses:** Less privacy-focused than Apple
- **Hub:** Nest Hub or Nest Hub Max

### Amazon Alexa

- **Best for:** Households with multiple Echo devices already in place
- **Strengths:** Largest device compatibility, lowest hardware cost
- **Weaknesses:** Voice recognition trails Google; UI is utilitarian
- **Hub:** Echo Hub or any Echo device

### Lutron Caséta (Lighting Specifically)

For lighting, Caséta is the gold standard regardless of which ecosystem you choose for everything else. It works with all three platforms via the Lutron bridge.

## What to Install First

If you're starting out, here's the sequence we recommend for Brooklyn homes:

### Phase 1: Foundation ($1,500–$3,500)

1. **Smart thermostat** (Ecobee, Nest, or Honeywell T9) — biggest energy ROI
2. **Smart switches on primary lighting** — living room, kitchen, primary bedroom
3. **Smart deadbolt + doorbell camera** — security + convenience
4. **WiFi mesh upgrade** — eliminate dead zones before adding more devices
5. **Smart smoke/CO detectors** — First Alert Onelink or Nest Protect

### Phase 2: Expansion ($1,500–$3,000)

1. **Smart blinds or shades** — Lutron Serena, Hunter Douglas PowerView
2. **Whole-home Lutron Caséta or Leviton Decora Smart on every switch**
3. **Smart sprinklers** (front garden or backyard) — Rachio, Hydrawise
4. **Smart leak detectors** — basement, washing machine, dishwasher

### Phase 3: Polish ($1,000–$3,000)

1. **Scene programming** — "goodnight," "morning," "movie," "leaving home"
2. **Automation rules** — lights on at sunset, thermostat back when no motion for 2 hours
3. **Security camera integration** — Ring, Nest, or Eufy
4. **Smart locks for short-term rental** properties (different keypad codes per guest)

## What a Whole-Home Brooklyn Smart Install Costs

| Scope | Typical Cost |
| --- | --- |
| Single-room conversion (4–6 switches, 1 dimmer) | $600–$1,100 |
| Foundation phase as above | $1,500–$3,500 |
| Whole-floor Caséta or Leviton smart switches (15–20 switches) | $2,800–$5,500 |
| Whole-home smart conversion (switches, thermostat, locks, cameras, blinds) | $6,000–$14,000 |

This includes parts, labor, NYC permits where required, and family training.

## Common Mistakes to Avoid

1. **Mixing too many ecosystems.** A Caséta dimmer, a Decora Smart switch, and a Kasa plug each have their own app. Pick one ecosystem and stick to it.
2. **Smart bulbs everywhere.** Smart bulbs cost 4× dumb bulbs and stop working when someone hits the wall switch. Smart switches are better for any fixture that's controlled by a wall switch.
3. **No-name brands.** Cloud-based smart devices from no-name brands sometimes lose their cloud (the company shuts down). Stick with Lutron, Leviton, Lutron, Apple, Google, Amazon, or established brands.
4. **Forgetting the WiFi.** A 60-device smart home overwhelms a single consumer router. Plan the network first.

## Why Use a Professional Electrician for Smart-Home Work

Most smart switches require:

- A live neutral (sometimes needs to be pulled)
- A switch box deep enough to accept the device
- Proper grounding of the equipment grounding conductor
- An LED-compatible dimming circuit when used as a dimmer

Doing this work without a professional electrician means working in a live panel without permits — a code violation in NYC that can void homeowners insurance. Doing it with a professional electrician means it's filed properly and your insurance stays valid.

## Our Process

1. **Discovery call** — we learn your routines, which devices you already own, and what you want automated
2. **Walkthrough and audit** — we open switch boxes, check for neutrals, measure WiFi
3. **Quote** — itemized by device, with platform recommendations
4. **Install day** — most homes take 1–2 days
5. **Family training** — we sit down with everyone in the house and walk through the app

Call Jetco at (667) 455-6368 or [contact us](/#contact) for a Brooklyn smart-home consultation.`,
  },
  {
    id: 112,
    slug: "brooklyn-christmas-light-installation-guide-cost-booking",
    title: "Brooklyn Christmas Light Installation: 2025 Guide to Design, Cost, and Booking",
    excerpt: "Professional Christmas light installation in Brooklyn — what it costs, when to book, how it works, and why hiring a professional electrician beats renting a ladder.",
    category: "Holiday Lighting",
    author: "Jetco Electrical Contractors",
    date: "December 6, 2025",
    readTime: "6 min read",
    image: "https://images.unsplash.com/photo-1789931123762-25ed9458c586?w=1600",
    content: `A fully lit Brooklyn brownstone during the holidays is one of the most photographed scenes in the city — but climbing a 24-foot extension ladder in December weather to pull it off is nobody's idea of fun. Here's exactly how professional Christmas light installation works in Brooklyn, what it costs in 2025, and why a professional electrician is the right person to do the job.

## Why Hire a Professional Electrician (Not a Handyman) for Holiday Lighting

Two things make Christmas light installation an electrical job, not a seasonal gig:

1. **Power.** A fully lit brownstone roofline, trees, and railings regularly draws 1,500–2,500 watts. On the wrong outlet or circuit, that's a tripped breaker on Christmas Eve — or a GFCI failure that kills every outdoor outlet. A professional electrician installs a dedicated weatherproof GFCI outlet sized for the load.
2. **Safety.** Mixed-weather ladder work above 20 feet is where a lot of insurance claims come from. OSHA-trained crews with the right insurance aren't optional for a serious install — they're the whole point of hiring out.

Our Brooklyn crew is OSHA-trained for ladder work up to 32 feet and carries full general liability and workers' comp. The homeowner has zero exposure.

## What a Professional Install Includes

- **Site visit and design** — we walk your property, listen to the look you want (warm white, multicolor, or animated), and sketch a plan
- **Commercial-grade LED strands** cut to length for your roofline (no visible extensions or jumble on the ground)
- **Roofline clips** that attach to shingles and gutters without screws, nails, or adhesive — zero damage
- **Weatherproof GFCI outlet** installed by a professional electrician if your home doesn't have one
- **Dusk-to-dawn photocell timer** so lights come on automatically every evening
- **In-season maintenance** — if a strand or bulb fails before New Year's, we're back within 48 hours
- **January takedown and labeled storage** so next year's install is faster and cheaper

## What Christmas Light Installation Costs in Brooklyn (2025)

| Property Type | Typical Range |
| --- | --- |
| Single brownstone roofline, no trees | $400–$750 |
| Full brownstone with railings and 1–2 trees | $800–$1,600 |
| Single-family home with 2+ rooflines + trees | $1,500–$2,800 |
| Large single-family with multi-roofline, trees, and detail work | $2,800–$4,500 |
| Small storefront roofline + awning | $500–$900 |
| Restaurant or boutique with full facade display | $1,200–$3,500 |

Pricing is turnkey: strands, clips, timer, installation, in-season maintenance, and January takedown. Lights you own already can lower the cost; a dedicated outdoor GFCI outlet add-on runs $250–$450 if you don't have one.

## When to Book

- **By early November:** Best availability for a pre-Thanksgiving install.
- **Mid-November to early December:** Still bookable, but calendar fills fast.
- **After December 10:** Install slots are usually sold out; book for next year.

Takedown is automatically scheduled for the first week of January.

## Design Choices That Matter

### Warm White vs. Multicolor vs. Animated

- **Warm white LEDs:** Classic, timeless, universally flattering on brownstone facades. The most popular Brooklyn choice.
- **Pure white LEDs (cool white):** Crisper, more modern — pairs well with contemporary buildings.
- **Multicolor LEDs:** Playful, family-friendly, great for homes with kids.
- **Animated/pixel-mapped (RGB):** Choreographed shows synced to music via smartphone — the most dramatic and the most expensive.

### Rooflines, Trees, Railings, and Trim

A balanced display uses 2–3 of these elements. All four at full intensity tends to look busy. Common Brooklyn brownstone winners:

- Roofline (warm white) + stoop railings (warm white) + a wrapped stoop tree
- Full roofline + 2 wrapped evergreens in the front garden
- Minimal roofline + heavy stoop/railing lighting + wreaths

### Timers and Smart Controls

A photocell timer (included) turns lights on at dusk and off at a set time (midnight is standard). We also offer smart WiFi controllers so you can:

- Flip lights on from your phone before a holiday party
- Schedule separate on/off times for weekends
- Pulse colors or sync animated lights to playlists

## Areas We Serve

We install Christmas lights across:

- **Brooklyn:** All neighborhoods — Park Slope, Carroll Gardens, Brooklyn Heights, Williamsburg, Greenpoint, Prospect Lefferts Gardens, Ditmas Park, Bay Ridge, Sunset Park, Bedford-Stuyvesant, Crown Heights, Flatbush, Sheepshead Bay, and more
- **Queens:** Astoria, Long Island City, Forest Hills, Rego Park, Jackson Heights
- **Staten Island:** North shore
- **Lower Manhattan:** South of 59th Street

## Why Jetco for Holiday Lighting

Most Christmas light companies disappear in the off-season. We don't — we're a full-time electrical contractor working year-round, and we treat holiday installs with the same accountability we bring to a 200A service upgrade. Full insurance, written warranty, labeled takedown, and the same crew back next year.

## How to Book

Call (667) 455-6368 or [send us a photo of your home](/#contact) via the contact form. We'll quote within 24 hours and lock in your install date.`,
  },
];
